import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  fetchCustomers,
  fetchCustomersStart,
  fetchCustomersSuccess,
  fetchCustomersFailure,
  fetchCustomerById,
  fetchCustomerByIdStart,
  fetchCustomerByIdSuccess,
  fetchCustomerByIdFailure,
  editCustomer,
  editCustomerStart,
  editCustomerSuccess,
  editCustomerFailure,
  deleteCustomer,
  deleteCustomerStart,
  deleteCustomerSuccess,
  deleteCustomerFailure,
  fetchCustomerScans,
  fetchCustomerScansStart,
  fetchCustomerScansSuccess,
  fetchCustomerScansFailure,
} from "../slices/customerSlice";

import { makeRequest } from "../../utils/api";
import { extractErrorMessage } from "../../utils/helpers";
import { toast } from "react-toastify";
import { Customer } from "../../types/pages/customer";

function* fetchCustomersSaga(
  action: PayloadAction<{
    page: number;
    rowsPerPage: number;
    sortBy?: string;
    sortOrder?: number;
  }>,
): Generator<any, void, any> {
  try {
    yield put(fetchCustomersStart());

    const skip = action.payload.page * action.payload.rowsPerPage;
    const limit = action.payload.rowsPerPage;

    const response = yield call(
      makeRequest,
      "get",
      `/customers/?skip=${skip}&limit=${limit}`,
    );

    yield put(
      fetchCustomersSuccess({
        data: response.data || response.customers || [],
        totalCount: response.total || response.count || 0,
        page: action.payload.page,
        limit: action.payload.rowsPerPage,
      }),
    );
  } catch (error: any) {
    yield put(
      fetchCustomersFailure(
        extractErrorMessage(error) || "Failed to load customers",
      ),
    );
  }
}

function* fetchCustomerByIdSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield put(fetchCustomerByIdStart());

    const response: Customer = yield call(
      makeRequest,
      "get",
      `/customers/${action.payload}`,
    );

    yield put(fetchCustomerByIdSuccess(response));
  } catch (error: any) {
    yield put(
      fetchCustomerByIdFailure(
        extractErrorMessage(error) || "Failed to fetch customer",
      ),
    );
  }
}

function* editCustomerSaga(
  action: PayloadAction<{
    customerId: string;
    customerData: Partial<Customer>;
  }>,
): Generator<any, void, any> {
  try {
    yield put(editCustomerStart());

    const response: Customer = yield call(
      makeRequest,
      "put",
      `/customers/${action.payload.customerId}`,
      action.payload.customerData,
    );

    yield put(editCustomerSuccess(response));
    toast.success("Customer updated successfully!");
  } catch (error: any) {
    yield put(
      editCustomerFailure(
        extractErrorMessage(error) || "Failed to update customer",
      ),
    );
  }
}

function* deleteCustomerSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield put(deleteCustomerStart());

    yield call(makeRequest, "delete", `/customers/${action.payload}`);

    yield put(deleteCustomerSuccess(action.payload));
    toast.success("Customer deleted successfully!");
  } catch (error: any) {
    yield put(
      deleteCustomerFailure(
        extractErrorMessage(error) || "Failed to delete customer",
      ),
    );
  }
}
function* fetchCustomerScansSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield put(fetchCustomerScansStart());

    const response = yield call(
      makeRequest,
      "get",
      `/customers/${action.payload}/scanned-products  `,
    );

    yield put(fetchCustomerScansSuccess(response.data || []));
  } catch (error: any) {
    yield put(
      fetchCustomerScansFailure(
        extractErrorMessage(error) || "Failed to fetch scans",
      ),
    );
  }
}

export default function* customersSaga(): Generator<any, void, any> {
  yield takeLatest(fetchCustomers.type, fetchCustomersSaga);
  yield takeLatest(fetchCustomerById.type, fetchCustomerByIdSaga);
  yield takeLatest(editCustomer.type, editCustomerSaga);
  yield takeLatest(deleteCustomer.type, deleteCustomerSaga);
  yield takeLatest(fetchCustomerScans.type, fetchCustomerScansSaga);
}
